import * as d3 from "d3";
import { useContext, useEffect, useState } from "react";
import { useQueryParams } from "use-query-params";
import { ConfigContext } from "./ConfigContext";
import { NATIONAL_PARAM, STATE_PARAM } from "./dropdowns/urlParams";
import { SOURCE_STATE_DATA_KEY } from "./helpers/config";
import { battlegroundMapping } from "./helpers/graphHelpers";
import {
  getFullStateName,
  getVoteSharePath,
  numberText,
} from "./helpers/helpers";

export default function VoteShare({
  viewType,
  state,
  filePath,
  yearLabels,
  editorialNotes,
  dynamicStateOptions = [],
}) {
  const setQuery = useQueryParams({})[1];
  const config = useContext(ConfigContext);
  const [voteShares, setVoteShares] = useState({});
  const [totalVotes, setTotalVotes] = useState({});

  const comparisonYear = yearLabels[0];
  const currentYear = yearLabels[yearLabels.length - 1];

  useEffect(() => {
    const url = getVoteSharePath(filePath);
    fetch(url).then((res) => {
      if (res.status !== 200) return;
      res.text().then((d) => {
        const data = d3.csvParse(d);
        const vs = {};
        const tv = {};
        data.forEach((d) => {
          vs[d[SOURCE_STATE_DATA_KEY]] = d["voteshare"];
          tv[d[SOURCE_STATE_DATA_KEY]] =
            d[config.VOTESHARE_CURRENT_YEAR_FIELD || "cy_votes"];
        });
        setVoteShares(vs);
        setTotalVotes(tv);
      });
    });
  }, [filePath, config.VOTESHARE_CURRENT_YEAR_FIELD]);

  const generateVoteShareContent = (
    voteshare,
    vote_total,
    locationName,
    isBattleground = false,
    bsStates = [],
    dynamicStateOptions = []
  ) => {
    return (
      <>
        In <strong>{locationName}</strong>, early voters cast{" "}
        <strong>{voteshare}%</strong> of the total votes counted in the{" "}
        {comparisonYear} general election.
        <br />
        At least{" "}
        <strong>
          <span style={{ fontSize: "1.3em" }}>{numberText(vote_total)}</span>
        </strong>{" "}
        voters cast early ballots in {isBattleground ? "these states" : "the"}{" "}
        {currentYear} election.
        {isBattleground && (
          <div className="mt-2">
            <strong>{locationName}: </strong>
            {bsStates.length > 0 ? (
              bsStates.map((s, i) => {
                const isStateInOptions = dynamicStateOptions.some(
                  (option) => option.value === s["State"]
                );
                return (
                  <span key={"bsStateLink" + i}>
                    {isStateInOptions ? (
                      <button
                        className="p-0 text-primary display-inline border-0"
                        onClick={() =>
                          setQuery(
                            { state: s["State"], view_type: STATE_PARAM },
                            "push"
                          )
                        }
                      >
                        {s["State_Name"]}
                      </button>
                    ) : (
                      <span>{s["State_Name"]}</span>
                    )}
                    {i !== bsStates.length - 1 && ", "}
                  </span>
                );
              })
            ) : (
              <span>No battleground states found</span>
            )}
          </div>
        )}
      </>
    );
  };

  let voteShareContent;
  let voteshare;
  let vote_total;

  if (viewType === NATIONAL_PARAM) {
    voteshare = voteShares[viewType];
    vote_total = totalVotes[viewType];
    voteShareContent = generateVoteShareContent(
      voteshare,
      vote_total,
      NATIONAL_PARAM
    );
  } else if (viewType === STATE_PARAM) {
    voteshare = voteShares[state];
    vote_total = totalVotes[state];
    voteShareContent = generateVoteShareContent(
      voteshare,
      vote_total,
      getFullStateName(state)
    );
  } else if (["PresBS", "SenateBS", "GovernorBS"].includes(viewType)) {
    // Needed to rename this to match the column name in editorial_notes.csv file
    if (viewType === "PresBS") {
      viewType = "PresidentBS";
    }
    voteshare = voteShares[viewType];
    vote_total = totalVotes[viewType];
    let bsStates;
    if (editorialNotes) {
      bsStates = editorialNotes.filter(
        (state) => state[`${viewType.replace("BS", "Battleground")}`] === "Y"
      );
    }
    voteShareContent = generateVoteShareContent(
      voteshare,
      vote_total,
      battlegroundMapping(viewType),
      true,
      bsStates,
      dynamicStateOptions
    );
  }

  if (!voteshare) return null;

  return <div>{voteShareContent}</div>;
}
